import { useMemo } from 'react';
import { pipe } from 'fp-ts/function';
import { array } from 'fp-ts';
import { getId } from '@mews/framework';
import { values } from '@mews/fp';
import { getResourceCategories } from '@mews-commander/legacy-entities/src/ducks/resourceCategories';
import { getUniqueServiceResourceFloorNumbers } from '@mews-commander/legacy-entities/src/selectors/spaces';
import { orderOrderableEntityArray } from '@mews-commander/legacy-entities/src/utils/orderableEntities';
import { getCurrentEnterpriseUserPreferredServiceId } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { useCommanderSelector } from '@mews-commander/utils';
import { TimelineUrlParams, TimelineUrlParamsKeys } from '../../schemas/timelineUrlParamsSchema';
import { TimelineFilterSchemaKeys, TimelineFilterValues } from './timelineFilterSchema';

export const useTimelineFilterPreselectedValues = (
    serviceId: string,
    unassignedReservationsEnabled?: boolean
): TimelineFilterValues => {
    const resourceCategories = useCommanderSelector((state) => getResourceCategories(state, { serviceId }));

    const uniqueServiceResourceFloorNumbers = useCommanderSelector((state) =>
        getUniqueServiceResourceFloorNumbers(state, { serviceId })
    );

    return useMemo(
        () => ({
            [TimelineFilterSchemaKeys.ServiceId]: serviceId,
            [TimelineFilterSchemaKeys.FloorNumber]: uniqueServiceResourceFloorNumbers,
            [TimelineFilterSchemaKeys.ResourceCategoryId]: pipe(
                resourceCategories,
                values,
                orderOrderableEntityArray,
                array.map(getId)
            ),
            [TimelineFilterSchemaKeys.ResourceFeatureId]: undefined,
            [TimelineFilterSchemaKeys.UnassignedReservationsEnabled]: unassignedReservationsEnabled,
        }),
        [serviceId, uniqueServiceResourceFloorNumbers, resourceCategories, unassignedReservationsEnabled]
    );
};

export const useTimelineFilterDefaultValuesMergedWithSearchParams = (
    searchParams: TimelineUrlParams
): TimelineFilterValues => {
    const defaultServiceId: string = useCommanderSelector(getCurrentEnterpriseUserPreferredServiceId);

    const {
        [TimelineUrlParamsKeys.ServiceId]: serviceId = defaultServiceId,
        [TimelineUrlParamsKeys.UnassignedReservationsEnabled]: unassignedReservationsEnabled,
    } = searchParams;
    const preselectedValues = useTimelineFilterPreselectedValues(serviceId, unassignedReservationsEnabled);

    return useMemo(
        () => ({
            ...preselectedValues,
            [TimelineFilterSchemaKeys.FloorNumber]:
                searchParams?.[TimelineUrlParamsKeys.FloorNumber] ??
                preselectedValues[TimelineFilterSchemaKeys.FloorNumber],
            [TimelineFilterSchemaKeys.ResourceCategoryId]:
                searchParams?.[TimelineUrlParamsKeys.ResourceCategoryId] ??
                preselectedValues[TimelineFilterSchemaKeys.ResourceCategoryId],
            [TimelineFilterSchemaKeys.ResourceFeatureId]: preselectedValues[TimelineFilterSchemaKeys.ResourceFeatureId],
            [TimelineFilterSchemaKeys.UnassignedReservationsEnabled]:
                searchParams?.[TimelineUrlParamsKeys.UnassignedReservationsEnabled] ??
                preselectedValues[TimelineFilterSchemaKeys.UnassignedReservationsEnabled],
        }),
        [preselectedValues, searchParams]
    );
};
